import { useCallback, useEffect, useRef, useState } from 'react';
import { WS_URL } from '../config/api';
import { getToken } from '../services/authApi';

/**
 * Gemini Live voice session over the backend proxy.
 *
 * Mic → 16 kHz mono PCM16 binary frames → backend → Gemini.
 * Gemini → 24 kHz mono PCM16 binary frames → backend → scheduled playback here.
 *
 * The two sample rates are NOT the same and mixing them up is the classic bug in
 * this stack: input is 16 kHz, output is 24 kHz. Chipmunk speech means they got
 * crossed.
 *
 * Everything the model decides arrives as JSON events; only audio is binary.
 */

const IN_RATE = 16000;
const OUT_RATE = 24000;

function downsampleTo16k(input, inputRate) {
    if (inputRate === IN_RATE) return input;
    const ratio = inputRate / IN_RATE;
    const out = new Float32Array(Math.floor(input.length / ratio));
    for (let i = 0; i < out.length; i++) {
        // Average the source window instead of point-sampling: plain decimation
        // aliases the rotor whine straight into the speech band.
        const start = Math.floor(i * ratio);
        const end = Math.min(input.length, Math.floor((i + 1) * ratio));
        let sum = 0;
        for (let j = start; j < end; j++) sum += input[j];
        out[i] = end > start ? sum / (end - start) : 0;
    }
    return out;
}

function floatTo16BitPCM(input) {
    const out = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
        const s = Math.max(-1, Math.min(1, input[i]));
        out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return out;
}

export function useVoiceLive({ selectedDroneId, onIntent, onFleet, onTurn, onFocus, halfDuplex = true }) {
    const [state, setState] = useState('idle');   // idle | connecting | live | error
    const [error, setError] = useState('');
    const [userText, setUserText] = useState('');
    const [modelText, setModelText] = useState('');

    const wsRef = useRef(null);
    const micCtxRef = useRef(null);
    const playCtxRef = useRef(null);
    const streamRef = useRef(null);
    const nodeRef = useRef(null);
    const nextPlayRef = useRef(0);
    const sourcesRef = useRef([]);
    // Callbacks change identity every render; read them through a ref so the
    // socket handler does not need re-binding (and cannot go stale).
    const cbRef = useRef({ onIntent, onFleet, onTurn, onFocus });
    useEffect(() => { cbRef.current = { onIntent, onFleet, onTurn, onFocus }; }, [onIntent, onFleet, onTurn, onFocus]);
    const halfDuplexRef = useRef(halfDuplex);
    useEffect(() => { halfDuplexRef.current = halfDuplex; }, [halfDuplex]);
    // True while model audio is still scheduled ahead of the playback clock.
    const isSpeakingRef = useRef(false);
    // The in-flight turn lives in refs, not state. Transcripts arrive as fragments
    // and the filing on turnComplete has to read the whole accumulated turn — doing
    // that from inside a setState updater made it run twice under StrictMode and
    // filed every turn twice.
    const userBufRef = useRef('');
    const modelBufRef = useRef('');

    const stopPlayback = useCallback(() => {
        for (const src of sourcesRef.current) {
            try { src.stop(); } catch { /* already finished */ }
        }
        sourcesRef.current = [];
        isSpeakingRef.current = false;
        if (playCtxRef.current) nextPlayRef.current = playCtxRef.current.currentTime;
    }, []);

    const playChunk = useCallback((arrayBuffer) => {
        const ctx = playCtxRef.current;
        if (!ctx) return;
        const int16 = new Int16Array(arrayBuffer);
        if (!int16.length) return;
        const buf = ctx.createBuffer(1, int16.length, OUT_RATE);
        const ch = buf.getChannelData(0);
        for (let i = 0; i < int16.length; i++) ch[i] = int16[i] / 32768;

        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(ctx.destination);
        // Schedule back to back on the audio clock. Playing each chunk on arrival
        // gives clicks and gaps; this is the jitter buffer.
        if (nextPlayRef.current < ctx.currentTime) nextPlayRef.current = ctx.currentTime;
        src.start(nextPlayRef.current);
        nextPlayRef.current += buf.duration;
        sourcesRef.current.push(src);
        isSpeakingRef.current = true;
        src.onended = () => {
            sourcesRef.current = sourcesRef.current.filter((s) => s !== src);
            // A short tail after the last buffer: the speaker is still ringing and
            // the room still has the tail of the sentence in it.
            if (sourcesRef.current.length === 0) {
                setTimeout(() => { if (sourcesRef.current.length === 0) isSpeakingRef.current = false; }, 250);
            }
        };
    }, []);

    const stop = useCallback(() => {
        stopPlayback();
        try { nodeRef.current?.disconnect(); } catch { /* gone */ }
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* gone */ }
        try { micCtxRef.current?.close(); } catch { /* gone */ }
        try { playCtxRef.current?.close(); } catch { /* gone */ }
        try { wsRef.current?.close(); } catch { /* gone */ }
        nodeRef.current = null;
        streamRef.current = null;
        micCtxRef.current = null;
        playCtxRef.current = null;
        wsRef.current = null;
        setState('idle');
    }, [stopPlayback]);

    const start = useCallback(async () => {
        if (state === 'connecting' || state === 'live') return;
        setError('');
        setState('connecting');
        try {
            if (!navigator.mediaDevices?.getUserMedia) {
                // getUserMedia is secure-context only, exactly like SpeechRecognition —
                // going Live does not dodge that requirement.
                throw new Error('Mic API is unavailable — https ya localhost pe kholo.');
            }

            const stream = await navigator.mediaDevices.getUserMedia({
                // Without echo cancellation the model hears its own voice through the
                // speakers and interrupts itself forever.
                audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });
            streamRef.current = stream;

            const token = getToken();
            const sep = WS_URL.includes('?') ? '&' : '?';
            const url = `${WS_URL}${sep}token=${encodeURIComponent(token || '')}&voice=1`
                + (selectedDroneId ? `&selected=${encodeURIComponent(selectedDroneId)}` : '');
            const ws = new WebSocket(url);
            ws.binaryType = 'arraybuffer';
            wsRef.current = ws;

            ws.onmessage = (event) => {
                if (event.data instanceof ArrayBuffer) {
                    playChunk(event.data);
                    return;
                }
                let msg;
                try { msg = JSON.parse(event.data); } catch { return; }
                switch (msg.type) {
                    case 'voice_ready': setState('live'); break;
                    // The primary live model was rejected and the proxy retried on
                    // the fallback. Not an error, but the operator should know the
                    // pinned model is gone.
                    case 'voice_fallback': setError(''); modelBufRef.current = ''; setModelText(''); break;
                    case 'voice_interrupted': stopPlayback(); break;
                    case 'voice_transcript':
                        if (msg.role === 'user') {
                            userBufRef.current += msg.text;
                            setUserText(userBufRef.current);
                        } else {
                            modelBufRef.current += msg.text;
                            setModelText(modelBufRef.current);
                        }
                        break;
                    case 'voice_turn_complete': {
                        // Close the turn: file what accumulated, then clear.
                        const u = userBufRef.current.trim();
                        const m = modelBufRef.current.trim();
                        userBufRef.current = '';
                        modelBufRef.current = '';
                        setUserText('');
                        setModelText('');
                        // Completed turns go to the caller, so the live session and
                        // the typed path share ONE transcript instead of two lists
                        // the operator has to read in parallel.
                        if (u) cbRef.current.onTurn?.({ role: 'user', text: u });
                        if (m) cbRef.current.onTurn?.({ role: 'model', text: m });
                        break;
                    }
                    case 'voice_intent': cbRef.current.onIntent?.(msg.intent); break;
                    case 'voice_fleet': cbRef.current.onFleet?.(msg.fleet); break;
                    case 'voice_focus': cbRef.current.onFocus?.(msg.droneId); break;
                    case 'voice_error':
                        setError(msg.message);
                        setState('error');
                        break;
                    case 'voice_closed':
                        if (msg.code && msg.code !== 1000) setError(`closed ${msg.code} ${msg.reason || ''}`);
                        break;
                    default: break;
                }
            };
            ws.onerror = () => { setError('voice socket error'); setState('error'); };
            ws.onclose = () => { setState((s) => (s === 'error' ? s : 'idle')); };

            const micCtx = new (window.AudioContext || window.webkitAudioContext)();
            micCtxRef.current = micCtx;
            const playCtx = new (window.AudioContext || window.webkitAudioContext)();
            playCtxRef.current = playCtx;
            nextPlayRef.current = playCtx.currentTime;

            const source = micCtx.createMediaStreamSource(stream);
            // ScriptProcessorNode is deprecated but works everywhere and is two lines.
            // AudioWorklet is the upgrade when this needs to leave the main thread.
            const node = micCtx.createScriptProcessor(2048, 1, 1);
            node.onaudioprocess = (e) => {
                if (ws.readyState !== WebSocket.OPEN) return;
                // Optional half-duplex. The browser's echo canceller does not
                // reliably cover WebAudio playback, so on speakers the model can
                // transcribe its own voice as operator speech and argue with
                // itself. Off by default — it costs barge-in, and it is a fix for
                // a problem you should confirm you have before paying for it.
                if (halfDuplexRef.current && isSpeakingRef.current) return;
                const pcm = floatTo16BitPCM(downsampleTo16k(e.inputBuffer.getChannelData(0), micCtx.sampleRate));
                ws.send(pcm.buffer);
            };
            source.connect(node);
            node.connect(micCtx.destination);
            nodeRef.current = node;
        } catch (err) {
            setError(err.message || String(err));
            setState('error');
            stop();
        }
    }, [state, selectedDroneId, playChunk, stopPlayback, stop]);

    const sendText = useCallback((text) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'text', text }));
        }
    }, []);

    // Tell the proxy whether the operator has auto-run on, so the tool result the
    // model reads matches what actually happens. Without this it kept saying
    // "confirm kijiye — command abhi run nahi hui" about commands that had
    // already run, and asked again on the next turn.
    const setAutoRun = useCallback((on) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'auto_run', on: !!on }));
        }
    }, []);

    useEffect(() => stop, [stop]);

    return {
        state, error, userText, modelText,
        start, stop, sendText, setAutoRun,
        clearText: () => { userBufRef.current = ''; modelBufRef.current = ''; setUserText(''); setModelText(''); },
    };
}

export default useVoiceLive;
